import React, { useState, useEffect } from "react";
import manGenie from "../../assets/images/manGenie.svg";
import styles from "./Chatbot.module.scss";
import genieLamp from "../../assets/images/genieLamp.svg";

import { Button } from "@mui/material";
import { useAppSelector } from "utils/hooks/storeHooks";
import { useNavigate } from "react-router-dom";
import { selectProviders } from "store/providers/selectors";
import { useDispatch } from "react-redux";
import { fetchAllProviders } from "store/providers/thunks";
import { setEmbedToken } from "store/session";

const ChatBot = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [messages, setMessages] = useState<any>([]);
  const providers = useAppSelector(selectProviders);
  const [isChatboxActive, setIsChatboxActive] = useState<any>(true);
  const [inputText, setInputText] = useState<any>("");
  const [currentReport, setCurrentReport] = useState<any>();
  const toggleChatbox = () => {
    setIsChatboxActive((prev: any) => !prev);
  };

  const handleEnterKeyPress = (event: { key: string }) => {
    if (event.key === "Enter") {
      handleSendButtonClick();
    }
  };

  function mapResponse(payload: any) {
    const transformedObject: any = {
      text: payload.custom.data
        ? payload.custom.data.find((item: any) => item.message)?.message || ""
        : payload.text,
      action:
        payload.custom.data.find((item: any) => item.action)?.action || "",
      provider:
        payload.custom.data.find((item: any) => item.provider)?.provider || "",
      filters: [],
    };

    const filtersData = payload.custom.data.find(
      (item: any) => item.filters
    )?.filters;
    if (filtersData) {
      const filtersArray = JSON.parse(filtersData.replace(/'/g, '"'));
      if (Array.isArray(filtersArray) && filtersArray.length > 0) {
        filtersArray.forEach((filter: any) =>
          transformedObject.filters.push(filter)
        );
      }
    }

    return transformedObject;
  }

  const handleSendButtonClick = () => {
    if (inputText.trim() !== "") {
      const userMessage = { sender: "user", message: inputText };
      const messagetxt = inputText.trim();
      setMessages((prevMessages: any) => [...prevMessages, userMessage]);
      sendMessageToRasa("user", messagetxt)
        .then((response: any) => {
          if (
            !response[0].custom &&
            response[0].text.startsWith("Execute PDF. NPI = ")
          ) {
            const regex = /NPI = (\d+)/;
            const match = response[0].text.match(regex);

            if (match) {
              const npiNumber = match[1];
              window.open(
                `https://ksdata.theksquaregroup.com/display_pdf/${npiNumber}`
              );
              response[0].text = "Right away...";
            }
          }
          if (response.length === 1) {
            const botMessage: any = {
              sender: "bot",
              message: response[0].text || mapResponse(response[0]).text,
            };
            if (response[0].custom) botMessage.data = mapResponse(response[0]);

            setMessages((prevMessages: any) => [...prevMessages, botMessage]);
          }

          if (response.length > 1) {
            let newResponse: any = [];
            let custom: any = {};
            response.forEach((message: any) => {
              if (message.custom) {
                newResponse.push({
                  recipient_id: "user",
                  text: message.custom.data[0].message,
                });

                custom = message;
                custom.custom.data[0].message = "";
              } else {
                newResponse.push(message);
              }
            });
            if (custom) newResponse.push(custom);

            newResponse.forEach((data: any) => {
              const botMessage: any = {
                sender: "bot",
                message: data.custom ? mapResponse(data).text : data.text,
              };
              if (data.custom) {
                botMessage.data = mapResponse(data);
              }

              setMessages((prevMessages: any) => [...prevMessages, botMessage]);
            });
          }
        })
        .catch((error) =>
          console.error("Error sending message to Rasa:", error)
        );

      setInputText("");
    }
  };

  const handleInputChange = (event: any) => {
    setInputText(event.target.value);
  };

  function removeDuplicateFilters(data: any) {
    const { filters } = data.data;
    const uniqueFilters: { [key: string]: any } = {};

    for (const filter of filters) {
      const { column_name } = filter;
      uniqueFilters[column_name] = filter;
    }

    data.data.filters = Object.values(uniqueFilters);

    return data;
  }

  const sendMessageToRasa = async (sender: string, message: string) => {
    try {
      const response = await fetch(
        "https://ksdata.theksquaregroup.com/chatbot/webhooks/rest/webhook",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            sender,
            message,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch response from Rasa server");
      }

      const data = await response.json();

      if (data && data.length > 0) {
        return data;
      }

      throw new Error("No response from Rasa server");
    } catch (error) {
      console.error("Error sending message to Rasa:", error);
      return { message: "An error occurred while processing your request." };
    }
  };

  const getProviderId = (providerName: string) => {
    if (providerName === "physician") return 1;
    if (providerName === "contract") return 2;
    if (providerName === "location") return 3;
    if (providerName === "taxonomy") return 4;
  };

  const handleLoadReport = (values: any) => {
    const providerId = getProviderId(values?.data.provider);
    const filtersByGenie = values?.data.filters.map((filter: any) => {
      return {
        tableName: filter.table_name,
        columnName: filter.column_name,
        values: filter.values,
      };
    });
    const providerReportId = providers.find(
      (provider: any) => provider?.id === providerId
    )?.reportId;
    const parsedInfo = {
      providerId: providerId,
      genieFilters: filtersByGenie,
      reportId: providerReportId,
    };
    navigate("/Graphs", { state: parsedInfo });
  };

  const handleAddFilter = (values: any) => {
    const providerId = getProviderId(values?.data?.provider);
    const providerReportId = providers.find(
      (provider: any) => provider?.id === providerId
    )?.reportId;
    const filtersByGenie = values?.data?.filters.map((filter: any) => {
      return {
        tableName: filter.table_name,
        columnName: filter.column_name,
        values: filter.values,
      };
    });
    const parsedInfo = {
      addFilter: true,
      providerId: providerId,
      genieFilters: filtersByGenie,
      reportId: providerReportId,
    };

    navigate("/Graphs", { state: parsedInfo });
  };

  useEffect(() => {
    if (messages[messages.length - 1]?.data?.action === "report") {
      setEmbedToken("");
      setCurrentReport(messages[messages.length - 1]);
      handleLoadReport(messages[messages.length - 1]);
    }
    if (
      messages[messages.length - 1]?.data?.action === "addfilter" &&
      messages[messages.length - 1]?.data?.provider ===
        currentReport?.data?.provider
    ) {
      messages[messages.length - 1].data.filters =
        currentReport?.data.filters.concat(
          messages[messages.length - 1].data.filters.filter((f: any) => f)
        );
      const filterParsed = removeDuplicateFilters(
        messages[messages.length - 1]
      );
      handleAddFilter(filterParsed);
    }
  }, [messages]);

  useEffect(() => {
    dispatch(fetchAllProviders());
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.chatbox}>
        {isChatboxActive && (
          <div className={styles.chatbox__support}>
            <div className={styles.chatbox__header}>
              <img
                width="64"
                height="64"
                src={manGenie}
                alt="doctor-skin-type-1"
              />
              <div className={styles.chatbox__content__header}>
                <h4 className={styles.chatbox__heading__header}>
                  Provider Genie
                </h4>
                <p className={styles.chatbox__description__header}>
                  Hi. I'm your genie. How can I help you?
                </p>
              </div>
            </div>
            <div className={styles.chatbox__messages}>
              {messages &&
                messages
                  .map((message: any, idx: number) => {
                    if (message.message !== "") {
                      const messageParts = message.message?.split("\n");
                      return (
                        <div
                          className={
                            message.sender === "user"
                              ? `${styles.messages__item__typing} ${styles.messages__item}`
                              : `${styles.messages__item__operator} ${styles.messages__item}`
                          }
                          key={idx}
                        >
                          {messageParts &&
                            messageParts.map((part: any, partIdx: number) => {
                              return (
                                <React.Fragment key={partIdx}>
                                  {part}
                                  {partIdx < messageParts.length - 1 && <br />}
                                </React.Fragment>
                              );
                            })}
                        </div>
                      );
                    }
                  })
                  .reverse()}
            </div>
            <div className={styles.chatbox__footer}>
              <input
                style={{ outline: "none" }}
                type="text"
                placeholder="Write a message..."
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleEnterKeyPress}
              />
              <Button
                variant="containedSmall"
                className={`${styles.chatbox__send__footer} ${styles.send__button}`}
                onClick={handleSendButtonClick}
              >
                Send
              </Button>
            </div>
          </div>
        )}
        <div className={styles.chatbox__button}>
          <button onClick={toggleChatbox}>
            <img
              src={genieLamp}
              style={{ width: "40px", height: "40px" }}
              alt="start"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
